// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  ReachabilityAnalysisRun,
  ReachabilityAnalysisRunsListOptionalParams,
  ReachabilityAnalysisRunsGetOptionalParams,
  ReachabilityAnalysisRunsGetResponse,
  ReachabilityAnalysisRunsCreateOptionalParams,
  ReachabilityAnalysisRunsCreateResponse,
  ReachabilityAnalysisRunsDeleteOptionalParams,
  ReachabilityAnalysisRunsDeleteResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a ReachabilityAnalysisRuns. */
export interface ReachabilityAnalysisRuns {
  /**
   * Gets list of Reachability Analysis Runs.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param options The options parameters.
   */
  list(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    options?: ReachabilityAnalysisRunsListOptionalParams,
  ): PagedAsyncIterableIterator<ReachabilityAnalysisRun>;
  /**
   * Gets Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsGetOptionalParams,
  ): Promise<ReachabilityAnalysisRunsGetResponse>;
  /**
   * Creates Reachability Analysis Runs.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param body Analysis Run resource object to create/update.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    body: ReachabilityAnalysisRun,
    options?: ReachabilityAnalysisRunsCreateOptionalParams,
  ): Promise<ReachabilityAnalysisRunsCreateResponse>;
  /**
   * Deletes Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  beginDelete(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<ReachabilityAnalysisRunsDeleteResponse>,
      ReachabilityAnalysisRunsDeleteResponse
    >
  >;
  /**
   * Deletes Reachability Analysis Run.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param workspaceName Workspace name.
   * @param reachabilityAnalysisRunName Reachability Analysis Run name.
   * @param options The options parameters.
   */
  beginDeleteAndWait(
    resourceGroupName: string,
    networkManagerName: string,
    workspaceName: string,
    reachabilityAnalysisRunName: string,
    options?: ReachabilityAnalysisRunsDeleteOptionalParams,
  ): Promise<ReachabilityAnalysisRunsDeleteResponse>;
}
